import { useEffect } from 'react';
import { CircleHelp } from 'lucide-react';
import clsx from 'clsx';
import { useUi } from '@/lib/store';

/** The "?" in page headers: opens the guide for the current page in the side panel (features/help/HelpPanel). */
export function HelpButton({ className, onDark }: { className?: string; onDark?: boolean }) {
  const open = useUi(s => s.helpPanel !== null);
  const openHelp = useUi(s => s.openHelp);
  const closeHelp = useUi(s => s.closeHelp);
  return (
    <button type="button" onClick={() => (open ? closeHelp() : openHelp('route'))} aria-pressed={open} aria-label="Help for this page" title="Help for this page (?)"
      data-testid="help-button"
      className={clsx('inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full no-print', onDark ? 'text-white/80 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white', open && 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-white', className)}>
      <CircleHelp className="h-5 w-5" />
    </button>
  );
}

/** "?" (Shift+/) anywhere outside a text field toggles help for the page. */
export function useHelpShortcut() {
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      // Pages with their own ? (the rota's keyboard shortcuts) call preventDefault, and win.
      if (e.key !== '?' || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      const s = useUi.getState();
      if (s.helpPanel) s.closeHelp(); else s.openHelp('route');
    };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, []);
}

/** Inline "Learn more" link for complex settings: opens a guide (optionally at a section) in the side panel. */
export function LearnMore({ article, anchor, children = 'Learn more' }: { article: string; anchor?: string; children?: React.ReactNode }) {
  const openHelp = useUi(s => s.openHelp);
  return (
    <button type="button" className="link inline-flex items-center gap-1 text-xs" data-testid="learn-more" onClick={() => openHelp(anchor ? `${article}#${anchor}` : article)}>
      <CircleHelp className="h-3.5 w-3.5" aria-hidden />{children}
    </button>
  );
}
